import { AxiosError, AxiosResponse } from 'axios'
import { useGeckoAuth } from 'features/auth'
import { RolodexContext } from 'features/rolodex'
import React, { useMemo } from 'react'
import useSWR from 'swr'
import {
  FetcherType,
  RolodexHistogramQuery,
  RolodexHistogramResponse,
} from 'types'

export function useRolodexHistogram(
  queryOrQueries:
    | RolodexHistogramQuery
    | ReadonlyArray<RolodexHistogramQuery>
    | undefined
): {
  data?: RolodexHistogramResponse[]
  isValidating: boolean
  isLoading: boolean
  hasEverLoaded: boolean
  error: AxiosError
  forceRefresh: () => Promise<void>
} {
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()
  const { apiURL } = React.useContext(RolodexContext)
  const queryBodiesStr = React.useMemo(() => {
    if (!queryOrQueries || !isAuthenticated) return '[]'
    const arr = Array.isArray(queryOrQueries)
      ? queryOrQueries
      : [queryOrQueries]
    const queryBodiesJSON = arr.map((body) => {
      const bodyCopy = { ...body }
      if (!bodyCopy.limit) bodyCopy.limit = 10000
      bodyCopy.tags = { ...bodyCopy.tags }
      return JSON.stringify(bodyCopy) // Ensures unique strings that work well with SWR
    })
    return JSON.stringify(queryBodiesJSON) // Allows this to be used in subsequent dep-arrays, even if queryOrQueries is not memo'd
  }, [queryOrQueries, isAuthenticated])

  // Doing this in 2 steps ensures that the caller doesn't have to memoize a `queryOrQueries` object
  const queryBodies: string[] = React.useMemo(
    () => JSON.parse(queryBodiesStr),
    [queryBodiesStr]
  )

  const fetcher = React.useCallback(
    (keys: string[]) =>
      Promise.all(
        keys.map(
          async (body) =>
            await axios
              .post(`${apiURL}/api/v2/entries/histogram`, body, {
                headers: {
                  'Content-Type': 'application/json',
                },
              })
              .then(async (response: AxiosResponse) => {
                const result = response.data
                if (!Array.isArray(result)) {
                  console.error(
                    'Rolodex entry fetch failed',
                    JSON.parse(body),
                    result
                  )
                  return []
                }
                return result
              })
        )
      ),
    [apiURL, axios]
  ) as FetcherType<RolodexHistogramResponse[]>

  const { data, isLoading, isValidating, error, mutate } = useSWR<
    RolodexHistogramResponse[]
  >(queryBodies, fetcher)
  const forceRefresh = React.useCallback(
    async () => await mutate().then(() => {}),
    [mutate]
  )

  return useMemo(
    () => ({
      data,
      isLoading,
      hasEverLoaded: !!data,
      error,
      forceRefresh,
      isValidating,
    }),
    [data, error, forceRefresh, isLoading, isValidating]
  )
}
